/**
 * Main App Component with React Router and SEO
 * Fully responsive and SEO-friendly setup
 */

import React, {useState, useEffect} from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Navbar from './components/NewNavbar';
import MenuAndSearchBar from './components/MenuAndSearchBar';
import Footer from './components/Footer';
import SEO from './components/SEO';
import ProductListingPage from './pages/ProductListingPage';
import HomePage from './pages/HomePage';
import ProductDetailPage from './pages/ProductDetailPage';
import CategoryListing from './pages/CategoryListing';
import CategoriesPage from './pages/CategoriesPage';
import BrandsPage from './pages/BrandsPage';
import BlogPage from './pages/BlogPage';
import BlogDetailPage from './pages/BlogDetailPage';
import ForkliftsPage from './static-page/ForkliftsPage';
import SkidSteerPage from './static-page/SkidSteerPage';
import TowerLightPage from './static-page/TowerLightPage';
import AirCompressorPage from './static-page/AirCompressorPage';
import TeleHandlerPage from './static-page/TeleHandlerPage';
import ExcavatorPage from './static-page/ExcavatorPage';
import BackhoePage from './static-page/BackhoePage';
import WheelLoaderPage from './static-page/WheelLoaderPage';
import CranePage from './static-page/CranePage';
import BulldozersPage from './static-page/BulldozersPage';
import ArialLiftsPage from './static-page/ArialLiftsPage';
import ContactPage from './pages/ContactPage';
import ThankYouPage from './pages/ThankYouPage';
import AboutPage from './pages/AboutPage';
import { NotFoundPage } from './pages/NotFoundPage';
import ScrollToTop from './components/ScrollToTop';
import EnquiryPopup from './components/EnquiryPopup';
import usePopupState from './hooks/usePopupState';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import StaticNavbar from './components/StaticNavbar';
import MiniExcavatorPage from './static-page/MiniExcavatorPage';
import GetAQuotePage from './static-page/GetAQuotePage';
import mixpanelService from './services/mixpanel';
import { getCookieConsent, hasConsent } from './utils/cookieConsent';
import CookieConsent from './components/CookieConsent';
import './App.css';

/**
 * Component to initialize analytics based on cookie consent
 * This runs once on app mount and checks if user has already given consent
 */
function AnalyticsInitializer() {
  useEffect(() => {
    // Check if user has already accepted cookies
    const consent = getCookieConsent();
    
    if (consent === "accepted") {
      try {
        mixpanelService.init?.();
        console.log("Analytics initialized - user has existing consent");
      } catch (e) {
        console.error("Mixpanel init failed:", e);
      }
    } else if (consent === "rejected") {
      console.log("Analytics disabled - user rejected cookies");
    } else {
      console.log("No cookie consent yet - waiting for user choice");
    }

    // Listen for consent changes (when user accepts/rejects from banner)
    const handleConsentChange = (event) => {
      const { consent } = event.detail;
      
      if (consent === "accepted") {
        // User just accepted - analytics already initialized in CookieConsent.jsx
        console.log("Consent accepted - analytics active");
      } else if (consent === "rejected") {
        console.log("Consent rejected - analytics will remain disabled");
      }
    };

    window.addEventListener('cookieConsentChanged', handleConsentChange);

    return () => {
      window.removeEventListener('cookieConsentChanged', handleConsentChange);
    };
  }, []);

  return null;
}

// Component to enforce lowercase URLs
function LowercaseRedirect({ children }) {
  const location = useLocation();
  const lowercasePath = location.pathname.toLowerCase();

  // Redirect if path contains uppercase letters
  if (location.pathname !== lowercasePath) {
    return <Navigate to={lowercasePath + location.search + location.hash} replace />;
  }

  return children;
}

// Component to conditionally render Navbar based on page type
function ConditionalNavbars() {
  const location = useLocation();

  // Define static page routes where StaticNavbar should appear
  const staticPageRoutes = [
    '/forklifts',
    '/skid-steer',
    '/lighting-towers',
    '/air-compressors',
    '/telehandlers',
    '/excavators',
    '/backhoe-loaders',
    '/wheel-loaders',
    '/all-terrain-cranes',
    '/bulldozers',
    '/arial-lifts',
    '/mini-excavator'
  ];

  // Check if current path is a static page
  const isStaticPage = staticPageRoutes.includes(location.pathname);

  return (
    <>
      {/* Regular Navbar - appears on all pages EXCEPT static pages */}
      {!isStaticPage && <Navbar  />}
      {/* Static Navbar - appears ONLY on static pages */}
      {isStaticPage && <StaticNavbar />}
    </>
  );
}

function App() {
  const { isPopupOpen, closePopup } = usePopupState({
    autoShow: true,
    delay: 2000,
    showOnce: false // Set to true if you want popup to show only once per session
  });
  const [currentProduct, setCurrentProduct] = useState(null);
  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        
        {/* Initialize analytics based on existing consent */}
        <AnalyticsInitializer />
        
        <div className="App">
          {/* Conditional Navigation Bars */}
          <ConditionalNavbars product={currentProduct} />
          <MenuAndSearchBar />

          {/* Main Content Area */}
          <main id="main-content" className="min-h-screen">
            <LowercaseRedirect>
              <Routes>
              {/* Home Page */}
              <Route path="/" element={
                <>
                  <SEO 
                    title="Golden Sparrow"
                    description="Find the best deals on industrial machinery. Buy, rent, or lease excavators, bulldozers, cranes, and more."
                  />
                  <HomePage />
                </>
              } />
              
              {/* Products Listing Page */}
              <Route path="/products" element={
                <>
                  <SEO 
                    title="Golden Sparrow | Collections"
                    description="Browse our extensive collection of industrial equipment for sale and rent. Filter by brand, condition, and more."
                  />
                  <ProductListingPage />
                </>
              } />
              
              {/* Categories Listing Page */}
              <Route path="/categories" element={
                <>
                  <SEO 
                    title="Golden Sparrow Categories - Browse by Type"
                    description="Explore machinery categories and find exactly what you need. Browse organized listings of construction and industrial equipment."
                  />
                  <CategoryListing />
                </>
              } />
              
              {/* Product Detail Page */}
              <Route path="/product/:productSlug/:productId" element={
                <>
                  <SEO title="Golden Sparrow | Product Details" />
                  <ProductDetailPage onProductLoad={setCurrentProduct} />
                </>
              } />
              
              {/* Other Pages */}
              
              <Route path="/brands" element={
                <>
                  <SEO title="Golden Sparrow | Brands" />
                  <BrandsPage />
                </>
              } />

              {/* Categories Routes */}
              <Route path="/categories" element={
                <>
                  <SEO 
                    title="Golden Sparrow | Categories - Buy & Rent Industrial Equipment"
                    description="Browse our comprehensive equipment categories. Find forklifts, excavators, cranes, and more industrial equipment for sale and rent."
                  />
                  <CategoriesPage />
                </>
              } />
              
              {/* Blog Routes */}
              <Route path="/blog" element={
                <>
                  <SEO 
                    title="Golden Sparrow | Blog News & Insights"
                    description="Stay updated with the latest industry trends, equipment insights, and news in the construction and industrial equipment sector."
                  />
                  <BlogPage />
                </>
              } />
              
              <Route path="/blog/:slug" element={
                <>
                  <BlogDetailPage />
                </>
              } />
              
              {/* Static Pages */}
              <Route path="/forklifts" element={
                <>
                  <SEO 
                    title="Golden Sparrow | Forklifts"
                    description=""
                  />
                  <ForkliftsPage />
                </>
              } />

              <Route path="/skid-steer" element={
                <>
                  <SEO 
                    title="Golden Sparrow | Skid Steer"
                    description=""
                  />
                  <SkidSteerPage />
                </>
              } />

              <Route path="/lighting-towers" element={
                <>
                  <SEO 
                    title="Golden Sparrow | Tower Lights"
                    description=""
                  />
                  <TowerLightPage />
                </>
              } />

              <Route path="/air-compressors" element={
                <>
                  <SEO 
                    title="Golden Sparrow | Air Compressor"
                    description=""
                  />
                  <AirCompressorPage />
                </>
              } />

              <Route path="/telehandlers" element={
                <>
                  <SEO 
                    title="Golden Sparrow | Telehandlers"
                    description=""
                  />
                  <TeleHandlerPage />
                </>
              } />

              <Route path="/excavators" element={
                <>
                  <SEO 
                    title="Golden Sparrow | Excavtor"
                    description=""
                  />
                  <ExcavatorPage />
                </>
              } />

              <Route path="/backhoe-loaders" element={
                <>
                  <SEO 
                    title="Golden Sparrow | Backhoe Loader"
                    description=""
                  />
                  <BackhoePage />
                </>
              } />

              <Route path="/wheel-loaders" element={
                <>
                  <SEO 
                    title="Golden Sparrow | Wheel Loader"
                    description=""
                  />
                  <WheelLoaderPage />
                </>
              } />

              <Route path="/all-terrain-cranes" element={
                <>
                  <SEO 
                    title="Golden Sparrow | Cranes"
                    description=""
                  />
                  <CranePage />
                </>
              } />

              <Route path='/bulldozers' element={
                <>
                <SEO title='Golden Sparrow | Bulldozers' description=''/>
                <BulldozersPage />
                </>
              } />

              <Route path='/arial-lifts' element={
                <>
                <SEO title='Golden Sparrow | Arial Lifts' description=''/>
                <ArialLiftsPage />
                </>
              } />

              <Route path='/mini-excavator' element={<><SEO title='Golden Sparrow | Mini Excavator'/> <MiniExcavatorPage /></>}/>
              <Route path='/get-a-quote' element={<><SEO title='Golden Sparrow | Get a Quote'/> <GetAQuotePage /></>}/>
              
              
              <Route path="/about-us" element={
                <>
                  <SEO title="Golden Sparrow | About Us" />
                  <AboutPage />
                </>
              } />

              <Route path='/privacy-policy' element={
                <>
                <SEO title='Golden Sparrow | Privacy Policy' />
                <PrivacyPolicy />
                </>
              } />

              <Route path='/terms-and-conditions' element={
                <>
                <SEO title='Golden Sparrow | Terms And Conditions' />
                <TermsAndConditions />
                </>
              } />
              
              <Route path="/contact" element={
                <>
                <SEO title='Golden Sparrow | Contact Us' />
                <ContactPage />
                </>
              } />
              
              {/* Thank You Page */}
              <Route path="/thank-you" element={
                <>
                  <SEO 
                    title="Golden Sparrow | Thank You"
                    description="Thank you for your enquiry. Our team will contact you shortly."
                  />
                  <ThankYouPage />
                </>
              } />
              
              {/* 404 Page */}
              <Route path="/404" element={
                <>
                  <SEO 
                    title="Golden Sparrow | Page Not Found"
                    description="The page you're looking for doesn't exist."
                  />
                  <NotFoundPage />
                </>
              } />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
            </LowercaseRedirect>
          </main>
          
          {/* Cookie Consent Banner - Shows only if user hasn't made a choice */}
          <CookieConsent />
          
          {/* Footer */}
          <Footer product={currentProduct} />

          {/* Enquiry Popup */}
          <EnquiryPopup isOpen={isPopupOpen} onClose={closePopup} />
        </div>
      </Router>
    </HelmetProvider>
  );
}

// Loading Page Component
function LoadingPage() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-neutral-600">Loading...</p>
      </div>
    </div>
  );
}

// Coming Soon Component for pages under development
function ComingSoonPage({ title }) {
  return (
    <div className="flex items-center justify-center min-h-[60vh] px-4">
      <div className="text-center">
        <h1 className="text-3xl sm:text-4xl font-display font-bold text-neutral-800 mb-4">{title}</h1>
        <p className="text-neutral-600">This page is coming soon!</p>
      </div>
    </div>
  );
}

export default App;